import React, { ReactNode } from 'react'

import { SimpleTable, TableSkeleton, TableHeader, DrilldownCell, Alert } from '@/components/ui'
import { useSkeletonOnConfigChange } from '@/hooks'
import useCompany from '@/hooks/useCompany'
import { validateDateRangeWithin13Months } from '@/insights/common/formatUtils'
import { buildQuery } from '@/insights/common/queryUtils'
import Caption, { addDateToCaptionFields } from '@/insights/components/Caption'
import { DateOptions } from '@/insights/components/CustomSingleSelect'
import { getSeverityLabel, severityCodes, SeverityNames } from '@/insights/components/SeverityDropdown'
import { createSeverityHeaders } from '@/insights/components/SeverityHeaderBuilder'
import {
  ApiService,
  type AssetMake,
  type InsightConfigurationType,
  type InsightDefinition,
  type InsightParametersType,
  InsightStatusCode,
  isInsightStatus,
} from '@/types/dashboard-types'
import { formatNumberLocale } from '@/utils'
import { downloadCsv } from '@/utils/csvExport'

import BarChart from './BarChart'

export interface ReeferAlarmCountByReeferMakeParams extends InsightParametersType {
  slug: string
  companyId?: number
  severity?: number[]
  geofenceIds?: number[]
  geofenceCategoryIds?: number[]
  date?: string
  from?: string
  to?: string
  makes?: string[]
  displayFormat?: string
}

const ReeferAlarmCountByReeferMake: InsightDefinition<ReeferAlarmCountByReeferMakeParams, AssetMake[]> = {
  active: true,
  slug: 'alarm/count-by-reefer-make',
  category: 'reeferAlarm',
  name: 'Reefer Alarm Count by Reefer Make',
  description: 'Display alarm counts by reefer make',
  defaultWidth: 'medium',
  defaultAspectRatio: 16 / 9,

  configuration: {
    transform: ({ parameters, field }) => {
      if (field === 'date' && parameters.date !== 'custom') {
        return { ...parameters, from: undefined, to: undefined }
      }
      if (field === 'geofenceIds') {
        return { ...parameters, geofenceCategoryIds: undefined }
      }
      if (field === 'geofenceCategoryIds') {
        return { ...parameters, geofenceIds: undefined }
      }

      return parameters
    },
    controls: [
      { id: 'companyId', control: 'CompanyDropdown', required: true },
      {
        id: 'makes',
        control: 'AssetMakeDropdown',
        required: true,
        label: 'Reefer Make',
        placeholder: 'Select reefer makes...',
      },
      {
        id: 'severity',
        control: 'SeverityDropdown',
      },
      { id: 'note', control: 'Label', label: 'Choose either Geofence or Geofence Category' },
      { id: 'geofenceIds', control: 'GeofenceDropdown', required: false },
      { id: 'geofenceCategoryIds', control: 'GeofenceCategoryDropdown', required: false },

      {
        id: 'date',
        control: 'CustomSingleSelect',
        options: DateOptions,
      },
      { id: 'dateRange', control: 'DateTimeRange', dependsOn: 'date', dependsOnValue: 'custom' },
      {
        id: 'displayFormat',
        control: 'CustomSingleSelect',
        options: [
          { label: 'Bar Chart', value: 'bar' },
          { label: 'Table', value: 'table', isDefault: true },
        ],
      },
    ],

    isValid: (configuration: ReeferAlarmCountByReeferMakeParams) => {
      if (configuration.date === 'custom') {
        if (configuration.from && configuration.to) {
          const fromDate = new Date(configuration.from)
          const toDate = new Date(configuration.to)
          if (fromDate > toDate) {
            return ' Start date must be before end date'
          }
        }
        const dateValidation = validateDateRangeWithin13Months(configuration.from, configuration.to)
        if (dateValidation !== true) {
          return dateValidation
        }
      }
      if (configuration?.companyId) {
        return true
      }
      return 'Company is required.'
    },
  },

  load: async (api: ApiService, parameters: ReeferAlarmCountByReeferMakeParams) => {
    //! Friendly reminder for Brian: Need to change the api path
    const path = `/insights/alarms/count-by-asset-make?${buildQuery(parameters)}`
    const response = await api.get<AssetMake[]>({
      path,
      origin: 'AlarmCountByReeferMake',
    })
    if (response.error) {
      return { code: InsightStatusCode.ERROR, message: response.message }
    }
    if (!response.data) {
      return { code: InsightStatusCode.NO_DATA }
    }
    return response.data
  },

  csv: async (api: ApiService, parameters: ReeferAlarmCountByReeferMakeParams, title: string) => {
    const path = '/insights/alarms/count-by-asset-make'
    await downloadCsv(api, path, parameters, title)
  },

  Display: ({ status, configuration, data }) => {
    const showSkeleton = useSkeletonOnConfigChange({ status, data, configuration })
    const parameters = configuration.parameters as ReeferAlarmCountByReeferMakeParams

    if (status.code === InsightStatusCode.NOT_CONFIGURED) {
      return <Alert type="info" message={status.message || 'Insight has not been configured'} />
    }

    if (showSkeleton) {
      return <TableSkeleton columns={2 + (parameters.severity?.length || 3)} withHeader rowHeight={35} />
    }
    if (status.code !== InsightStatusCode.DISPLAYING && status.code !== InsightStatusCode.LOADING) {
      // Wait until we're ready to display before displaying
      // Framework will handle the other states like error and no data.
      // We could handle no data here with a custom message like
      // "No alarms found for severity critical".
      return null
    }
    if (isInsightStatus(data)) {
      return null
    }
    if (!data?.length) {
      // Probably would never get here, but keeps typescript happy.
      return null
    }
    const severities = (
      parameters.severity?.length
        ? parameters.severity.map((s) => getSeverityLabel(String(s)).toLowerCase())
        : ['critical', 'warning', 'informational']
    ) as SeverityNames[]

    const sortedDisplayData = [...data].sort((a, b) => {
      const aValue = Number(a.total || 0)
      const bValue = Number(b.total || 0)

      return bValue - aValue
    })

    if (configuration.parameters.displayFormat === 'bar') {
      return <BarChart data={sortedDisplayData} configuration={configuration} />
    }

    // Create table body for the new SimpleTable format
    const body = sortedDisplayData?.map((item) => {
      const result: Record<string, string | DrilldownCell | ReactNode> = {
        key: item.make,
        Make: <span className="flex pl-1">{item.make}</span>,
      }
      severities.forEach((sev) => {
        const code = severityCodes.find((s) => s.label.toLowerCase() === sev)?.value
        const ddConfig: InsightConfigurationType = {
          ...configuration,
          slug: 'alarm/alarms-detail',
          title: `${configuration.title}: Details`,
          parameters: {
            ...parameters,
            displayFormat: 'table',
            severity: code,
            makes: item.make,
          },
        }
        result[sev] = {
          value: formatNumberLocale(Number(item[sev])),
          className: 'block pr-5 w-full text-center',
          configuration: ddConfig,
          id: `${item.make}-${sev}`,
        }
      })
      return result
    })

    const sevHeaders = createSeverityHeaders({
      selectedSeverities: severities,
      includeIcons: true,
      includeTotal: false,
    }) as TableHeader[]
    const headers = ['Make', ...sevHeaders]
    const headerClassNames = sevHeaders.reduce((h, s) => Object.assign(h, { [s.key]: 'w-[100px]' }), {})

    return <SimpleTable headers={headers} body={body} headerClassNames={headerClassNames} />
  },

  Caption: ({ configuration, status }) => {
    const { companies } = useCompany()
    if (status.code === InsightStatusCode.ERROR) {
      // Framework will handle this
      return 'Error'
    }
    const fields: Record<string, string | React.ReactNode> = {}
    const params = configuration.parameters as ReeferAlarmCountByReeferMakeParams
    if (params.companyId) {
      const company = companies.find((c) => c.id === params.companyId)
      if (company) {
        fields['Company'] = company.name
      }
    }
    if (params.severity?.length) {
      if (params.severity.length === 3) {
        fields['Severity'] = 'All'
      } else {
        const labels = params.severity
          .map((s) => getSeverityLabel(s.toString()))
          .filter((l) => !!l)
          .join(', ')
        fields['Severity'] = labels
      }
    }
    if (params.makes) {
      if (params.makes.length === 1) {
        fields['Make'] = params.makes[0]
      } else {
        fields['Makes'] = (
          <span data-tip={params.makes.join(', ')} className="tooltip tooltip-top">
            {params.makes.length} Makes
          </span>
        )
      }
    }
    addDateToCaptionFields({ ...params, fields, finite: true })
    if (Object.keys(fields).length) {
      return <Caption fields={fields} />
    }
    return ''
  },

  Footer: ({ configuration, data, status }) => {
    if (status.code === InsightStatusCode.LOADING && data === undefined) {
      return ''
    }
    if (status.code === InsightStatusCode.NO_DATA) {
      return <div>No records found</div>
    }
    const rows = data?.length
    if (!rows) {
      return <div>No records found</div>
    }
    if (configuration.parameters.displayFormat === 'bar') {
      const totalAlarms = data.reduce((acc, curr) => acc + (Number(curr.total) || 0), 0)
      return <div>{formatNumberLocale(totalAlarms)} alarms found</div>
    }
    return (
      <div>
        {rows} row{rows === 1 ? '' : 's'} found
      </div>
    )
  },
}

export default ReeferAlarmCountByReeferMake
